/**
 * Normalizes category / legend labels from API data so Highcharts always receives
 * a non-empty display string.
 *
 * - The API sometimes returns HTML-encoded fragments (e.g. `Identify &amp; Authenticate`);
 *   we decode those to plain text first.
 * - Only `<` and `>` are escaped for Highcharts: category axes and many labels render as
 *   SVG/plain text, so escaping `&` to `&amp;` was shown literally (e.g. `&amp;` on the x-axis).
 *   HTML tooltips/dataLabels that need `&` escaped apply their own escaping (e.g. escapeHtmlForTooltip).
 */

export const CHART_UNKNOWN_LABEL = 'Unknown';

/**
 * Decode common HTML entities from API strings (iterates to undo mild double-encoding).
 * `&amp;` is decoded last so sequences like `&amp;lt;` become `<` correctly.
 */
export function decodeIncomingChartLabelText(text: string): string {
  let prev = '';
  let s = text;
  while (s !== prev) {
    prev = s;
    s = s
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#x27;/gi, "'")
      .replace(/&#0*39;/g, "'")
      .replace(/&apos;/gi, "'")
      .replace(/&amp;/gi, '&');
  }
  return s;
}

/**
 * Escape `<` / `>` so values like `<unknown>` are not parsed as markup where Highcharts uses HTML.
 * Do not escape `&` here — SVG category labels show `&amp;` literally if we do.
 */
export function escapeChartLabelForHighcharts(text: string): string {
  return text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function normalizeChartDisplayLabel(value: any): string {
  if (value === null || value === undefined) {
    return CHART_UNKNOWN_LABEL;
  }
  const s = typeof value === 'string' ? value.trim() : String(value).trim();
  if (s.length === 0) {
    return CHART_UNKNOWN_LABEL;
  }
  const low = s.toLowerCase();
  if (low === 'null' || low === 'undefined') {
    return CHART_UNKNOWN_LABEL;
  }
  return escapeChartLabelForHighcharts(decodeIncomingChartLabelText(s));
}

/** Preserve array length: each slot becomes a displayable label (never dropped). */
export function normalizeCategoryArray(categories: any[] | null | undefined): string[] {
  if (!Array.isArray(categories)) {
    return [];
  }
  return categories.map((c) => normalizeChartDisplayLabel(c));
}

function isLikelyTimestampString(val: any): boolean {
  if (typeof val !== 'string') {
    return false;
  }
  const t = Date.parse(val);
  return !isNaN(t) && (val.includes('T') || /\d{4}-\d{2}-\d{2}/.test(val));
}

function normalizeDataPointForChart(item: any, chartType: string): any {
  const ct = chartType.toLowerCase();
  if (item === null || item === undefined) {
    return item;
  }
  if (typeof item !== 'object') {
    return item;
  }
  if (Array.isArray(item)) {
    if (ct === 'scatter' || ct === 'bubble') {
      return item;
    }
    if (ct === 'heatmap' && item.length >= 3) {
      return [
        normalizeChartDisplayLabel(item[0]),
        normalizeChartDisplayLabel(item[1]),
        ...item.slice(2),
      ];
    }
    if ((ct === 'pie' || ct === 'donut') && item.length >= 2) {
      return [normalizeChartDisplayLabel(item[0]), ...item.slice(1)];
    }
    if (ct === 'sankey' && item.length >= 3) {
      return [
        normalizeChartDisplayLabel(item[0]),
        normalizeChartDisplayLabel(item[1]),
        ...item.slice(2),
      ];
    }
    if (item.length >= 2) {
      const first = item[0];
      if (typeof first === 'number') {
        return item;
      }
      if (isLikelyTimestampString(first)) {
        return item;
      }
      return [normalizeChartDisplayLabel(first), ...item.slice(1)];
    }
    return item;
  }

  const o = { ...item };
  const needsPointName =
    ct === 'pie' || ct === 'donut' || ct === 'treemap';
  if ('name' in o || 'label' in o || needsPointName) {
    const raw = o.name ?? o.label;
    o.name = normalizeChartDisplayLabel(raw ?? '');
    if ('label' in o) {
      o.label = o.name;
    }
  }
  return o;
}

/**
 * Deep-normalize Highcharts options from the custom-dashboard API: xAxis categories,
 * series names, and point names used by legend, tooltips, and category axes.
 */
export function normalizeHighchartsApiConfig(config: any, chartTypeHint?: string): any {
  if (!config || typeof config !== 'object') {
    return config;
  }
  const chartType = String(chartTypeHint || config.chart?.type || '').toLowerCase();
  const next: any = { ...config };

  const normXa = (xa: any) => {
    if (!xa || typeof xa !== 'object') {
      return xa;
    }
    const x = { ...xa };
    if (Array.isArray(x.categories)) {
      x.categories = normalizeCategoryArray(x.categories);
    }
    return x;
  };

  if (Array.isArray(next.xAxis)) {
    next.xAxis = next.xAxis.map(normXa);
  } else if (next.xAxis) {
    next.xAxis = normXa(next.xAxis);
  }

  if (Array.isArray(next.yAxis)) {
    next.yAxis = next.yAxis.map(normXa);
  } else if (next.yAxis) {
    next.yAxis = normXa(next.yAxis);
  }

  if (Array.isArray(next.series)) {
    next.series = next.series.map((series: any) => {
      if (!series || typeof series !== 'object') {
        return series;
      }
      const s = { ...series };
      s.name = normalizeChartDisplayLabel(s.name ?? s.label ?? s.Name ?? '');
      if (s.label !== undefined) {
        s.label = s.name;
      }
      if (Array.isArray(s.data)) {
        s.data = s.data.map((pt: any) => normalizeDataPointForChart(pt, chartType));
      }
      return s;
    });
  }

  return next;
}
